import React, { useCallback, useMemo, useState } from 'react';
import {
  Modal, Upload, Button, Table, Select, Switch, Tag, message,
  Space, Typography, Alert, Progress
} from 'antd';
import { InboxOutlined, ImportOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';

import { calendarAPI } from '../utils/api';

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;

// Patrones que indican fecha movible (recurring debe ser false).
const MOVIBLE_PATTERNS = [
  /ordinario/i, /adviento/i, /cuaresma/i, /pascua/i, /\bdomingo\b/i,
  /bautismo del señor/i, /ceniza/i, /\bramos\b/i, /ascensión/i,
  /pentecostés/i, /\btrinidad\b/i, /corpus/i, /cristo rey/i, /sagrado corazón/i
];
// Excepciones (santos cuyo nombre contiene "Domingo" o similar pero son fecha fija).
const MOVIBLE_EXCEPTIONS = [/domingo savio/i, /domingo de guzmán/i, /domingo de guzman/i];

const isMovable = (title) => {
  if (!title) return false;
  if (MOVIBLE_EXCEPTIONS.some(rx => rx.test(title))) return false;
  return MOVIBLE_PATTERNS.some(rx => rx.test(title));
};

// Convierte una celda de Excel (Date, número o string) a 'YYYY-MM-DD'.
const toISODate = (value) => {
  if (!value && value !== 0) return null;
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof value === 'number') {
    // Serial de Excel — SheetJS lo convierte con dateNF; por seguridad, parseamos.
    const dt = XLSX.SSF.parse_date_code(value);
    if (!dt) return null;
    return `${dt.y}-${String(dt.m).padStart(2, '0')}-${String(dt.d).padStart(2, '0')}`;
  }
  const s = String(value).trim();
  // Formatos aceptados: 'YYYY-MM-DD' o 'DD/MM/YYYY'
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dmy = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${String(dmy[2]).padStart(2, '0')}-${String(dmy[1]).padStart(2, '0')}`;
  }
  return null;
};

const CalendarImportModal = ({ open, onClose, onImported }) => {
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  const [defaultEventType, setDefaultEventType] = useState('santoral');
  const [defaultColor, setDefaultColor] = useState('blue');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setFileName('');
    setRows([]);
    setResult(null);
    setImporting(false);
  }, []);

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const parseFile = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array', cellDates: true });

        // Elegir la hoja preferida.
        const preferred = wb.SheetNames.find(n => /calendario\s*completo/i.test(n));
        const sheetName = preferred || wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];

        // Parseamos como array de objetos con nombres de columnas del header.
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false, dateNF: 'yyyy-mm-dd' });

        // Buscamos columnas por nombre flexible.
        const findKey = (obj, candidates) => {
          const keys = Object.keys(obj);
          for (const c of candidates) {
            const k = keys.find(k => k.toLowerCase().trim() === c);
            if (k) return k;
          }
          return null;
        };

        if (raw.length === 0) {
          message.warning('La hoja está vacía');
          return;
        }
        const sample = raw[0];
        const dateKey = findKey(sample, ['fecha', 'date']);
        const titleKey = findKey(sample, ['celebración / acontecimiento', 'celebración', 'celebracion', 'title', 'título', 'titulo']);

        if (!dateKey || !titleKey) {
          message.error(`No encuentro columnas obligatorias. Necesito "Fecha" y "Celebración". Hojas: ${wb.SheetNames.join(', ')}`);
          return;
        }

        const parsed = raw
          .map((r, idx) => {
            const iso = toISODate(r[dateKey]);
            const title = String(r[titleKey] || '').trim();
            return {
              key: idx,
              date: iso,
              title,
              event_type: defaultEventType,
              color: defaultColor,
              recurring: !isMovable(title),
              _skip: !iso || !title
            };
          })
          .filter(r => !r._skip);

        setFileName(`${file.name} — hoja "${sheetName}"`);
        setRows(parsed);
        setResult(null);
        message.success(`${parsed.length} filas leídas`);
      } catch (err) {
        console.error(err);
        message.error('Error leyendo el archivo: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // evita el upload automático
  }, [defaultEventType, defaultColor]);

  const updateRow = (key, patch) => {
    setRows(prev => prev.map(r => r.key === key ? { ...r, ...patch } : r));
  };

  const removeRow = (key) => {
    setRows(prev => prev.filter(r => r.key !== key));
  };

  const stats = useMemo(() => ({
    total: rows.length,
    recurring: rows.filter(r => r.recurring).length,
    noRecurring: rows.filter(r => !r.recurring).length
  }), [rows]);

  const handleImport = async () => {
    if (rows.length === 0) {
      message.warning('No hay filas para importar');
      return;
    }
    setImporting(true);
    setResult(null);
    try {
      const events = rows.map(r => ({
        date: r.date,
        title: r.title,
        event_type: r.event_type,
        color: r.color,
        recurring: r.recurring
      }));
      const res = await calendarAPI.bulkImport(events);
      if (res.success) {
        setResult(res.data);
        message.success(res.message);
        onImported?.();
      } else {
        message.error(res.error || 'Error en importación');
      }
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.error || 'Error de red al importar');
    } finally {
      setImporting(false);
    }
  };

  const columns = [
    {
      title: 'Fecha', dataIndex: 'date', width: 120,
      render: (v) => <Text code>{v}</Text>
    },
    {
      title: 'Título', dataIndex: 'title', ellipsis: true
    },
    {
      title: 'Tipo', dataIndex: 'event_type', width: 150,
      render: (v, row) => (
        <Select
          value={v}
          size="small"
          style={{ width: '100%' }}
          onChange={(val) => updateRow(row.key, { event_type: val })}
          options={[
            { value: 'santoral', label: 'Santoral' },
            { value: 'santo_agustino', label: 'Santo agustino' },
            { value: 'evento', label: 'Evento' }
          ]}
        />
      )
    },
    {
      title: 'Recurrente', dataIndex: 'recurring', width: 130,
      render: (v, row) => (
        <Space>
          <Switch
            size="small"
            checked={v}
            onChange={(val) => updateRow(row.key, { recurring: val })}
          />
          {!v && <Tag color="orange" style={{ fontSize: 10 }}>Movible</Tag>}
        </Space>
      )
    },
    {
      title: '', width: 60,
      render: (_, row) => (
        <Button size="small" danger type="link" onClick={() => removeRow(row.key)}>
          Quitar
        </Button>
      )
    }
  ];

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={<><ImportOutlined /> Importar calendario desde Excel/CSV</>}
      width={950}
      footer={[
        <Button key="close" onClick={handleClose}>Cerrar</Button>,
        <Button
          key="import"
          type="primary"
          icon={<ImportOutlined />}
          loading={importing}
          disabled={rows.length === 0}
          onClick={handleImport}
        >
          Importar {rows.length > 0 ? `(${rows.length})` : ''}
        </Button>
      ]}
    >
      <Paragraph type="secondary">
        Se leerá la hoja <strong>"Calendario completo"</strong> si existe, o la primera hoja.
        Columnas esperadas: <Text code>Fecha</Text> y <Text code>Celebración / acontecimiento</Text>.
        La casilla <strong>Recurrente</strong> se marca automáticamente según el título
        (los movibles como Ordinario/Adviento/Cuaresma/Pascua se detectan y quedan sin recurrencia).
      </Paragraph>

      <Space style={{ marginBottom: 12 }}>
        <span>Tipo por defecto:</span>
        <Select
          value={defaultEventType}
          onChange={setDefaultEventType}
          style={{ width: 170 }}
          options={[
            { value: 'santoral', label: 'Santoral' },
            { value: 'santo_agustino', label: 'Santo agustino' },
            { value: 'evento', label: 'Evento' }
          ]}
        />
        <span>Color:</span>
        <Select
          value={defaultColor}
          onChange={setDefaultColor}
          style={{ width: 120 }}
          options={[
            { value: 'blue', label: 'Azul' },
            { value: 'red', label: 'Rojo' },
            { value: 'green', label: 'Verde' },
            { value: 'purple', label: 'Morado' },
            { value: 'orange', label: 'Naranja' }
          ]}
        />
      </Space>

      {rows.length === 0 ? (
        <Dragger
          beforeUpload={parseFile}
          multiple={false}
          maxCount={1}
          showUploadList={false}
          accept=".xlsx,.xls,.csv"
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">Suelta aquí el Excel o CSV, o haz clic para elegir</p>
          <p className="ant-upload-hint">Formatos soportados: .xlsx, .xls, .csv</p>
        </Dragger>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              <Space split={<span style={{ opacity: 0.4 }}>·</span>}>
                <span><strong>{fileName}</strong></span>
                <span>{stats.total} filas</span>
                <span>{stats.recurring} recurrentes</span>
                <span>{stats.noRecurring} movibles</span>
                <Button size="small" onClick={reset}>Elegir otro archivo</Button>
              </Space>
            }
          />

          {result && (
            <Alert
              type={result.error_count > 0 ? 'warning' : 'success'}
              showIcon
              style={{ marginBottom: 12 }}
              message={`Importación: ${result.inserted_count} insertados, ${result.skipped_count} duplicados omitidos, ${result.error_count} errores`}
            />
          )}

          {importing && <Progress percent={99} status="active" showInfo={false} style={{ marginBottom: 12 }} />}

          <Table
            columns={columns}
            dataSource={rows}
            size="small"
            pagination={{ pageSize: 20, showSizeChanger: true }}
            scroll={{ y: 380 }}
          />
        </>
      )}
    </Modal>
  );
};

export default CalendarImportModal;
